/**
 * Canonical module (page) keys for the admin-level permission system.
 *
 * MIRRORED FROM backend Packages/user-service/src/config/moduleKeys.js —
 * keep the two files in sync. Keys are APPEND-ONLY: never rename a shipped
 * key. If a key must change, add an alias below instead — historical DB rows
 * and old cached payloads keep sending the old spelling.
 */

export const CANONICAL_PAGES = [
  "payroll",
  "attendance",
  "leaves",
  "documents",
  "reports",
  "advancements",
  "calendar",
  "messages",
  "configurations",
  "adminLevels",
  "employees",
  "changesPage",
  "complaints",
  "departments",
  "groups",
];

// input (lowercased, trimmed) → canonical key
export const MODULE_ALIASES = {
  payroll: "payroll",
  payrollmanagement: "payroll",
  "payroll-management": "payroll",
  payroll_management: "payroll",

  attendance: "attendance",
  attendancemanagement: "attendance",
  "attendance-management": "attendance",
  attendance_management: "attendance",

  leaves: "leaves",
  leavemanagement: "leaves",
  "leave-management": "leaves",
  leave_management: "leaves",

  documents: "documents",
  reports: "reports",

  advancements: "advancements",
  advancement: "advancements",
  advancementmanagement: "advancements",
  "advancement-management": "advancements",
  advancement_management: "advancements",
  salaryadvancements: "advancements",
  solfa: "advancements",

  calendar: "calendar",
  messages: "messages",
  configurations: "configurations",

  adminlevels: "adminLevels",
  "admin-levels": "adminLevels",
  admin_levels: "adminLevels",

  employees: "employees",
  employeemanagement: "employees",
  "employee-management": "employees",
  employee_management: "employees",

  changespage: "changesPage",
  "changes-page": "changesPage",
  changes_page: "changesPage",

  complaints: "complaints",
  complaint: "complaints",
  escalation: "complaints",
  escalations: "complaints",
  escalationmapping: "complaints",
  "escalation-mapping": "complaints",
  escalation_mapping: "complaints",
  "escalation mapping": "complaints",
  "escalation group mapping": "complaints",

  departments: "departments",
  department: "departments",
  departmentmanagement: "departments",
  "department-management": "departments",
  department_management: "departments",

  groups: "groups",
  group: "groups",
  groupsmanagement: "groups",
  "groups-management": "groups",
  groups_management: "groups",
};

/**
 * Normalize any module spelling to its canonical key.
 * Idempotent: normalizeModuleKey(normalizeModuleKey(x)) === normalizeModuleKey(x).
 * Unknown keys pass through lowercased+trimmed so new modules degrade gracefully.
 */
export const normalizeModuleKey = (moduleName) => {
  const key = String(moduleName || "").trim().toLowerCase();
  return MODULE_ALIASES[key] || key;
};

/** Case/alias-insensitive module comparison — always use this, never ===. */
export const moduleKeyEquals = (a, b) =>
  normalizeModuleKey(a) === normalizeModuleKey(b);

// ── Client-level (superadmin) module switches ────────────────────────────────
// MIRRORED FROM backend config/moduleKeys.js — keep in sync. Every canonical
// page maps to exactly ONE boolean on the client's CompanyModulePermission row
// (GET /api/configurations/:clientName). A page whose switch is OFF for the
// client is hidden and blocked for EVERY role in that client — admin, manager,
// admin-level and plain user alike. Only superadmin bypasses (see AuthContext).
// Pages not listed here (dashboard, settings, profile) are never client-gated.
export const CLIENT_MODULE_FLAGS = {
  payroll: "payrollManagement",
  attendance: "attendanceManagement",
  leaves: "leaveManagement",
  documents: "documentRequests",
  reports: "reportsModule",
  advancements: "advancementRequests",
  calendar: "calendarModule",
  messages: "privateMessaging",
  configurations: "systemSettings",
  adminLevels: "roleManagement",
  employees: "employeeManagement",
  changesPage: "employeeActions",
  complaints: "complaintsModule",
  departments: "departmentsModule",
  groups: "groupsModule",
};

/** The client row column that gates a module, or null if the page is ungated. */
export const clientFlagForModule = (moduleName) =>
  CLIENT_MODULE_FLAGS[normalizeModuleKey(moduleName)] || null;

/**
 * Pure check against an already-loaded client row.
 *   - unknown/ungated module → enabled
 *   - no row loaded         → enabled (nothing to enforce yet)
 *   - flag missing on row   → enabled (older cached payload)
 *   - flag === false        → disabled
 */
export const isModuleEnabledForClientRow = (row, moduleName) => {
  const flag = clientFlagForModule(moduleName);
  if (!flag) return true;
  if (!row || typeof row !== "object") return true;
  return row[flag] !== false;
};
